import { useMemo } from 'react'
import { FaArrowRight } from 'react-icons/fa'
import { Button, Column, DataTable, Section, StackCell, StatusPill } from '../../../../../ui'
import { ClientData, formatDate, getFormattedAddress, getFullName } from '../shared'
import { ProvenancePill } from './provenancePill'

interface MatchesTableProps {
    results: ClientData[]
    onSelect: (client: ClientData) => void
}

const rowKey = (c: ClientData) => `${c.origin}-${c.client_id}`

/** Several clients matched: a scannable table, one row per record, click to open. */
export const MatchesTable = ({ results, onSelect }: MatchesTableProps) => {
    const databases = useMemo(() => new Set(results.map((r) => r.origin)).size, [results])

    const columns: Column<ClientData>[] = [
        {
            key: 'client',
            header: 'Client',
            sortable: true,
            sortValue: (r) => getFullName(r),
            render: (r) => <StackCell primary={getFullName(r)} secondary={r.email} />,
        },
        {
            key: 'dob',
            header: 'Date of birth',
            nowrap: true,
            sortable: true,
            render: (r) => formatDate(r.dob),
        },
        { key: 'telephone', header: 'Telephone', nowrap: true, render: (r) => r.telephone || '—' },
        {
            key: 'address',
            header: 'Address',
            width: '24%',
            render: (r) => getFormattedAddress(r.current_address) || '—',
        },
        {
            key: 'origin',
            header: 'Source',
            sortable: true,
            nowrap: true,
            sortValue: (r) => `${r.provenance?.legacy ? 1 : 0}-${r.provenance?.system ?? r.origin}`,
            // Just the pill here; the database and full provenance detail are on the record itself.
            render: (r) => (
                <span title={`${r.origin} database`}>
                    <ProvenancePill provenance={r.provenance} fallback={r.origin} size="sm" />
                </span>
            ),
        },
        {
            key: 'cfa',
            header: 'CFA',
            nowrap: true,
            sortable: true,
            sortValue: (r) => (r.cfa_signed ? 1 : 0),
            render: (r) =>
                r.cfa_signed ? (
                    <StatusPill tone="good" dot>
                        Signed
                    </StatusPill>
                ) : (
                    <StatusPill tone="warn" dot>
                        Unsigned
                    </StatusPill>
                ),
        },
        {
            key: 'claims',
            header: 'Claims',
            align: 'right',
            sortable: true,
            sortValue: (r) => r.claims?.length ?? 0,
            render: (r) => r.claims?.length ?? 0,
        },
        {
            key: 'open',
            header: '',
            align: 'right',
            nowrap: true,
            render: (r) => (
                <Button
                    size="sm"
                    iconRight={<FaArrowRight />}
                    onClick={(e) => {
                        e.stopPropagation()
                        onSelect(r)
                    }}
                >
                    Open
                </Button>
            ),
        },
    ]

    return (
        <Section
            title="Matching clients"
            description={`${results.length} records matched across ${databases} ${
                databases === 1 ? 'database' : 'databases'
            }. Select a record to open it.`}
        >
            <DataTable
                columns={columns}
                rows={results}
                rowKey={rowKey}
                onRowClick={onSelect}
                emptyTitle="No matching clients"
            />
        </Section>
    )
}
